import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { Send, Sparkles, User, Trash2, Moon, Activity } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const COACH_PROMPTS = [
  "Review my portfolio",
  "Analyze my recent trades",
  "Why am I losing money?",
  "How to improve win rate?",
];

const NYX_GRADIENT = 'linear-gradient(135deg, #6366f1, #8b5cf6)';

// ─── Weekly quota ring ────────────────────────────────────────────────────────
function QuotaRing({ used, limit, unlimited }) {
  const r    = 7;
  const circ = 2 * Math.PI * r;
  if (unlimited || !limit) {
    return (
      <svg width="18" height="18" viewBox="0 0 18 18" style={{ flexShrink: 0 }} title="Unlimited messages">
        <circle cx="9" cy="9" r={r} fill="none" stroke="#8b5cf640" strokeWidth="2.5" />
        <circle cx="9" cy="9" r={r} fill="none" stroke="#8b5cf6" strokeWidth="2.5"
          strokeDasharray={circ} strokeDashoffset={0} strokeLinecap="round" />
      </svg>
    );
  }
  const pct  = Math.min(used / limit, 1);
  const dash = circ * (1 - pct);
  const color = pct >= 1 ? '#ef4444' : pct >= 0.75 ? '#f59e0b' : '#8b5cf6';
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" style={{ transform: 'rotate(-90deg)', flexShrink: 0 }}
      title={`${used}/${limit} messages this week`}>
      <circle cx="9" cy="9" r={r} fill="none" stroke="#374151" strokeWidth="2.5" />
      <circle cx="9" cy="9" r={r} fill="none" stroke={color} strokeWidth="2.5"
        strokeDasharray={circ} strokeDashoffset={dash} strokeLinecap="round"
        style={{ transition: 'stroke-dashoffset 0.3s ease' }} />
    </svg>
  );
}

// ─── Main page ─────────────────────────────────────────────────────────────────
function AICoachPage() {
  const { colors }  = useTheme();
  const location    = useLocation();

  const [marketCtx, setMarketCtx] = useState(false);
  const [messages, setMessages] = useState(() => {
    try {
      const saved = localStorage.getItem('nyx_messages');
      if (saved) return JSON.parse(saved);
    } catch {}
    return [{
      role: 'assistant',
      content: "Hello, I'm Nyx, your AI Trading Coach. I have access to your full trading profile — capital, discipline score, trade history, and behavioral patterns. What would you like to work on?",
      timestamp: new Date().toISOString(),
    }];
  });
  const [inputValue, setInputValue]     = useState('');
  const [coachLoading, setCoachLoading] = useState(false);
  const [contextLoading, setContextLoading] = useState(false);
  const [sessionId, setSessionId]       = useState(() => localStorage.getItem('nyx_session_id') || null);
  const [nyxQuota, setNyxQuota] = useState({ used: 0, limit: null, unlimited: true });
  const messagesEndRef = useRef(null);
  const inputRef       = useRef(null);

  const formatTime = (ts) => new Date(ts).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Kolkata' });

  useEffect(() => {
    if (location.state?.marketContext) setMarketCtx(true);
    if (location.state?.prefill) {
      setInputValue(location.state.prefill);
      inputRef.current?.focus();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);
  useEffect(() => { try { localStorage.setItem('nyx_messages', JSON.stringify(messages)); } catch {} }, [messages]);
  useEffect(() => {
    axios.get(`${API_URL}/ai/coach/quota`).then(r => setNyxQuota(r.data)).catch(() => {});
  }, []);

  const handleSend = async (text = inputValue) => {
    if (!text.trim() || coachLoading) return;
    const userMessage = text.trim();
    setMessages(p => [...p, { role: 'user', content: userMessage, timestamp: new Date().toISOString() }]);
    setInputValue('');
    setCoachLoading(true);

    try {
      let currentSessionId = sessionId;
      if (!currentSessionId) {
        setContextLoading(true);
        const sessionRes = await axios.post(`${API_URL}/ai/coach/session/start`);
        currentSessionId = sessionRes.data.session_id;
        setSessionId(currentSessionId);
        localStorage.setItem('nyx_session_id', currentSessionId);
        setContextLoading(false);
      }

      const res = await axios.post(`${API_URL}/ai/coach/chat`, {
        session_id: currentSessionId,
        message: userMessage,
        with_market: marketCtx,
      });

      if (res.data.session_id === null) { setSessionId(null); localStorage.removeItem('nyx_session_id'); }
      if (res.data.quota) setNyxQuota(res.data.quota);
      setMessages(p => [...p, { role: 'assistant', content: res.data.response, timestamp: new Date().toISOString() }]);
    } catch (err) {
      if (err?.response?.status === 429) {
        const detail = err.response?.data?.detail || 'Weekly Nyx limit reached. Upgrade your plan to continue.';
        setMessages(p => [...p, { role: 'assistant', content: detail, timestamp: new Date().toISOString(), isError: true }]);
        axios.get(`${API_URL}/ai/coach/quota`).then(r => setNyxQuota(r.data)).catch(() => {});
      } else {
        setMessages(p => [...p, { role: 'assistant', content: "I'm having trouble right now. Please try again.", timestamp: new Date().toISOString(), isError: true }]);
      }
    } finally {
      setCoachLoading(false);
      setContextLoading(false);
      inputRef.current?.focus();
    }
  };

  const handleClear = async () => {
    if (coachLoading) return;
    if (sessionId) { try { await axios.delete(`${API_URL}/ai/coach/session/${sessionId}`); } catch {} }
    setMessages([{ role: 'assistant', content: "Chat cleared. Ready to help.", timestamp: new Date().toISOString() }]);
    setSessionId(null);
    localStorage.removeItem('nyx_session_id');
    localStorage.removeItem('nyx_messages');
  };

  const isAtLimit = !nyxQuota.unlimited && nyxQuota.limit && nyxQuota.used >= nyxQuota.limit;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 160px)' }}>

      {/* ── Header ── */}
      <div style={{
        borderRadius: 16, marginBottom: 10, padding: '12px 14px',
        background: 'linear-gradient(135deg, #6366f118 0%, #8b5cf610 100%)',
        border: '1px solid #6366f128',
        position: 'relative', overflow: 'hidden',
      }}>
        <div style={{ position: 'absolute', top: -30, right: -30, width: 100, height: 100, borderRadius: '50%', background: 'radial-gradient(circle, #8b5cf618 0%, transparent 70%)', pointerEvents: 'none' }} />
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ width: 38, height: 38, borderRadius: 12, background: NYX_GRADIENT, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, boxShadow: '0 4px 14px rgba(99,102,241,0.4)' }}>
              <Moon style={{ width: 18, height: 18, color: '#fff' }} />
            </div>
            <div>
              <p style={{ fontSize: 15, fontWeight: 800, color: colors.primaryText, margin: 0, letterSpacing: '-0.01em' }}>Nyx AI Coach</p>
              <p style={{ fontSize: 11, color: colors.secondaryText, margin: 0 }}>Discipline · Risk · Psychology</p>
            </div>
          </div>
          {/* Quota pill */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '4px 10px', borderRadius: 20, background: 'rgba(139,92,246,0.1)', border: '1px solid rgba(139,92,246,0.25)' }}>
            <QuotaRing used={nyxQuota.used} limit={nyxQuota.limit} unlimited={nyxQuota.unlimited} />
            <span style={{ fontSize: 10, fontWeight: 700, color: '#a78bfa' }}>
              {nyxQuota.unlimited ? '∞' : `${nyxQuota.used}/${nyxQuota.limit}`}
            </span>
          </div>
        </div>

        {/* Market context toggle inside header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 10 }}>
          <button
            onClick={() => setMarketCtx(v => !v)}
            style={{
              display: 'flex', alignItems: 'center', gap: 6,
              padding: '5px 12px', borderRadius: 20, fontSize: 11, fontWeight: 700, cursor: 'pointer',
              background: marketCtx ? NYX_GRADIENT : 'rgba(255,255,255,0.06)',
              color: marketCtx ? '#fff' : colors.secondaryText,
              border: `1px solid ${marketCtx ? 'transparent' : 'rgba(255,255,255,0.1)'}`,
              transition: 'all 0.15s',
            }}
          >
            <Activity style={{ width: 11, height: 11 }} />
            Market Context {marketCtx ? 'ON' : 'OFF'}
          </button>
          {marketCtx && (
            <span style={{ fontSize: 10, color: '#8b5cf6' }}>Personal + 90-day regime</span>
          )}
        </div>
      </div>

      {/* ── Message area ── */}
      <div style={{
        flex: 1, overflowY: 'auto', borderRadius: 14, padding: 10,
        display: 'flex', flexDirection: 'column', gap: 8,
        background: `linear-gradient(135deg, ${colors.accent}14 0%, ${colors.accent}06 100%)`,
        border: `1px solid ${marketCtx ? '#6366f140' : colors.accentBorder}`,
      }}>
        {messages.map((msg, idx) => (
          <div key={idx} style={{ display: 'flex', justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start' }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 6, maxWidth: '88%', flexDirection: msg.role === 'user' ? 'row-reverse' : 'row' }}>
              <div style={{ width: 22, height: 22, borderRadius: 7, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, background: msg.role === 'assistant' ? NYX_GRADIENT : colors.accent }}>
                {msg.role === 'user' ? <User style={{ width: 11, height: 11, color: '#fff' }} /> : <Moon style={{ width: 11, height: 11, color: '#fff' }} />}
              </div>
              <div style={{
                borderRadius: 12, padding: '8px 11px',
                background: msg.role === 'user'
                  ? `linear-gradient(135deg, ${colors.accent} 0%, ${colors.accent}cc 100%)`
                  : msg.isError ? 'rgba(239,68,68,0.1)' : colors.inputBg,
                border: msg.isError ? '1px solid rgba(239,68,68,0.25)' : 'none',
              }}>
                <p style={{ fontSize: 13, lineHeight: 1.6, color: msg.role === 'user' ? '#fff' : colors.primaryText, margin: 0, whiteSpace: 'pre-wrap' }}>{msg.content}</p>
                <p style={{ fontSize: 9, marginTop: 3, opacity: 0.5, color: msg.role === 'user' ? '#fff' : colors.secondaryText, margin: '3px 0 0' }}>{formatTime(msg.timestamp)}</p>
              </div>
            </div>
          </div>
        ))}

        {coachLoading && (
          <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 6 }}>
              <div style={{ width: 22, height: 22, borderRadius: 7, display: 'flex', alignItems: 'center', justifyContent: 'center', background: NYX_GRADIENT }}>
                <Moon style={{ width: 11, height: 11, color: '#fff' }} />
              </div>
              <div style={{ borderRadius: 12, padding: '8px 12px', background: colors.inputBg }}>
                {contextLoading ? (
                  <p style={{ fontSize: 12, color: colors.secondaryText, margin: 0 }}>Loading your trading profile...</p>
                ) : (
                  <div style={{ display: 'flex', gap: 4 }}>
                    {[0, 150, 300].map(d => (
                      <div key={d} style={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: '#8b5cf6', animation: `bounce 1s ease-in-out ${d}ms infinite` }} />
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* ── Quick prompt chips ── */}
      {messages.length <= 2 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
          {COACH_PROMPTS.map((p, i) => (
            <button key={i} onClick={() => handleSend(p)} disabled={coachLoading}
              style={{
                display: 'flex', alignItems: 'center', gap: 5,
                padding: '5px 11px', borderRadius: 20, fontSize: 12, fontWeight: 600, cursor: 'pointer',
                background: 'rgba(139,92,246,0.1)', border: '1px solid rgba(139,92,246,0.25)',
                color: '#a78bfa',
              }}>
              <Sparkles style={{ width: 10, height: 10 }} />
              {p}
            </button>
          ))}
        </div>
      )}

      {/* ── Input bar ── */}
      <div style={{
        marginTop: 8, display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px',
        borderRadius: 14,
        background: `linear-gradient(135deg, ${colors.accent}14 0%, ${colors.accent}06 100%)`,
        border: `1px solid ${marketCtx ? '#6366f140' : colors.accentBorder}`,
      }}>
        <input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={e => setInputValue(e.target.value)}
          onKeyPress={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
          placeholder={isAtLimit ? 'Weekly limit reached — upgrade to continue' : 'Ask Nyx about your trading...'}
          style={{ flex: 1, background: 'none', border: 'none', outline: 'none', fontSize: 13, color: colors.primaryText, padding: '2px 4px' }}
          disabled={coachLoading || isAtLimit}
        />
        <button
          onClick={handleClear}
          style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 30, height: 30, borderRadius: 9, background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)', cursor: 'pointer', flexShrink: 0 }}
          title="Clear chat">
          <Trash2 style={{ width: 13, height: 13, color: colors.secondaryText }} />
        </button>
        <button
          onClick={() => handleSend()}
          disabled={!inputValue.trim() || coachLoading || isAtLimit}
          style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            width: 34, height: 34, borderRadius: 10, flexShrink: 0, cursor: 'pointer',
            background: inputValue.trim() && !isAtLimit ? NYX_GRADIENT : 'rgba(255,255,255,0.06)',
            border: 'none', opacity: (!inputValue.trim() || isAtLimit) ? 0.4 : 1,
            boxShadow: inputValue.trim() && !isAtLimit ? '0 3px 12px rgba(99,102,241,0.4)' : 'none',
            transition: 'all 0.15s',
          }}>
          <Send style={{ width: 14, height: 14, color: '#fff' }} />
        </button>
      </div>

      <p style={{ fontSize: 10, textAlign: 'center', marginTop: 5, color: colors.secondaryText }}>
        Nyx provides educational guidance only · Not financial advice
      </p>
    </div>
  );
}

export default AICoachPage;
